import { useCallback, useEffect, useRef, useState } from 'react';
import { processImage } from '../lib/image';

export default function Camera({ onAdd, onError }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const wakeRef = useRef(null);
  const [facing, setFacing] = useState('environment');
  const [status, setStatus] = useState('starting');
  const [flash, setFlash] = useState(false);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    wakeRef.current?.release().catch(() => {});
    wakeRef.current = null;
  }, []);

  const start = useCallback(async () => {
    stop();
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unavailable');
      return;
    }
    setStatus('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setStatus('ready');
      if (navigator.wakeLock?.request) {
        wakeRef.current = await navigator.wakeLock.request('screen').catch(() => null);
      }
    } catch (error) {
      const denied = error?.name === 'NotAllowedError' || error?.name === 'SecurityError';
      setStatus(denied ? 'denied' : 'unavailable');
      onError(denied
        ? 'Camera permission was denied. Allow camera access in browser settings, or add photos from your device.'
        : 'The camera could not start. It may be in use by another app. Close it there or add photos from your device.');
    }
  }, [facing, onError, stop]);

  useEffect(() => {
    start();
    const visibility = () => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      stop();
    };
  }, [start, stop]);

  const capture = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d', { alpha: false }).drawImage(video, 0, 0);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      if (!blob) throw new Error('The photo could not be captured.');
      setFlash(true);
      window.setTimeout(() => setFlash(false), 120);
      navigator.vibrate?.(30);
      onAdd(await processImage(blob));
    } catch (error) {
      onError(error.message);
    }
  };

  return (
    <section className="camera">
      <video ref={videoRef} muted playsInline className="camera-video" />
      {flash && <div className="camera-flash" aria-hidden="true" />}
      {status !== 'ready' && (
        <div className="camera-message">
          {status === 'starting' ? 'Starting camera…' : 'Camera unavailable. Add photos from your device below.'}
        </div>
      )}
      <button
        type="button"
        className="switch-camera"
        aria-label="Switch front or rear camera"
        onClick={() => setFacing((value) => value === 'environment' ? 'user' : 'environment')}
      >
        Switch
      </button>
      <button type="button" className="shutter" aria-label="Take photo" disabled={status !== 'ready'} onClick={capture}>
        <span />
      </button>
    </section>
  );
}